/**
 * Parsed CMS rich text that contains a list (e.g. tour/course steps).
 *
 * Why parse instead of just `v-html`-ing the CMS `<ul>/<li>` markup?
 * Callers (e.g. EventImportantDetails' steps section) need each `<li>` as its
 * own Vue item — for plain-bullet-list styling matching the structured
 * "included" block — rather than one opaque v-html blob.
 *
 * CMS content often prefixes the list with a `<p>` (intro) and may continue
 * with more prose after the list (e.g. a closing remark). Both are returned
 * separately from `items` so they render in the same text style as step copy
 * without becoming list items themselves.
 */
export interface ParsedHtmlList {
  /** Meaningful HTML before the list, or null when empty / whitespace-only. */
  preambleHtml: string | null
  items: string[]
  /** Meaningful HTML after the list, or null when empty / whitespace-only. */
  afterHtml: string | null
}

interface BalancedTagMatch {
  /** Index of the opening tag's `<`. */
  start: number
  /** Index right after the opening tag's `>`. */
  contentStart: number
  /** Index of the matching closing tag's `<`. */
  contentEnd: number
  /** Index right after the matching closing tag's `>`. */
  end: number
}

/**
 * Finds the first `<tagName>...</tagName>` block in `html`, tracking nesting
 * depth so a nested tag of the *same* name (e.g. a sub-list `<ul>` inside a
 * `<li>`, or a nested `<li>` inside that sub-list) isn't mistaken for the
 * outer block's closing tag.
 */
const findBalancedTag = (
  html: string,
  tagName: string
): BalancedTagMatch | null => {
  const openTagRegex = new RegExp(`<${tagName}(\\s[^>]*)?>`, 'i')
  const openMatch = html.match(openTagRegex)
  if (!openMatch || openMatch.index === undefined) {
    return null
  }

  const start = openMatch.index
  const contentStart = start + openMatch[0].length

  const tagRegex = new RegExp(
    `<${tagName}(?:\\s[^>]*)?>|</${tagName}\\s*>`,
    'gi'
  )
  tagRegex.lastIndex = contentStart

  let depth = 1
  let match: RegExpExecArray | null
  while ((match = tagRegex.exec(html)) !== null) {
    if (match[0].startsWith('</')) {
      depth -= 1
      if (depth === 0) {
        return {
          start,
          contentStart,
          contentEnd: match.index,
          end: match.index + match[0].length,
        }
      }
    } else {
      depth += 1
    }
  }

  return null
}

/** Splits `html` into its top-level `<li>` blocks, keeping nested sub-lists intact. */
const extractTopLevelListItems = (html: string): string[] => {
  const items: string[] = []
  let offset = 0

  while (offset < html.length) {
    const liMatch = findBalancedTag(html.slice(offset), 'li')
    if (!liMatch) {
      break
    }

    const inner = html
      .slice(offset + liMatch.contentStart, offset + liMatch.contentEnd)
      .trim()
    if (inner) {
      items.push(inner)
    }

    offset += liMatch.end
  }

  return items
}

/**
 * Extract the first top-level `<ul>`/`<ol>`, plus any HTML before and after it.
 * Returns null when the content is not a list (caller should fall back to raw HTML).
 */
export const parseHtmlList = (html: unknown): ParsedHtmlList | null => {
  if (typeof html !== 'string' || !html.trim()) {
    return null
  }

  const ulMatch = findBalancedTag(html, 'ul')
  const olMatch = findBalancedTag(html, 'ol')
  const listMatch =
    ulMatch && olMatch
      ? ulMatch.start <= olMatch.start
        ? ulMatch
        : olMatch
      : ulMatch || olMatch

  if (!listMatch) {
    return null
  }

  const listContent = html.slice(listMatch.contentStart, listMatch.contentEnd)
  const items = extractTopLevelListItems(listContent)

  if (items.length === 0) {
    return null
  }

  const preambleRaw = html.slice(0, listMatch.start).trim()
  const preambleHtml =
    preambleRaw && stripHtmlToPlainText(preambleRaw) ? preambleRaw : null

  const afterRaw = html.slice(listMatch.end).trim()
  const afterHtml = afterRaw && stripHtmlToPlainText(afterRaw) ? afterRaw : null

  return { preambleHtml, items, afterHtml }
}

/** Strip tags and decode common entities for length checks / truncated plain text. */
export const stripHtmlToPlainText = (html: unknown): string => {
  if (typeof html !== 'string') {
    return ''
  }

  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|h[1-6])>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim()
}
